import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, ArchiveBoxXMarkIcon, ArrowUturnLeftIcon, XCircleIcon, RectangleGroupIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import type { Department } from '../../types/timesheets';

function DepartmentModal({ department, onClose }: { department: Department | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(department?.name ?? '');
  const [sortOrder, setSortOrder] = useState(String(department?.sort_order ?? 0));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const sort = Number(sortOrder) || 0;
      return department
        ? api.adminUpdateDepartment(department.id, { name, sortOrder: sort })
        : api.adminCreateDepartment({ name, sortOrder: sort });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'departments'] });
      qc.invalidateQueries({ queryKey: ['departments'] });
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? 'Could not save department'),
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">{department ? 'Edit Department' : 'New Department'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><XCircleIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Engineering"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Sort order</label>
            <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none" />
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>
        <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
          <button onClick={() => save.mutate()} disabled={!name.trim() || save.isPending}
            className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg disabled:opacity-50 transition-colors">
            {save.isPending ? 'Saving…' : department ? 'Save Changes' : 'Create Department'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminDepartmentsPage() {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Department | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const { data: departments = [], isLoading } = useQuery({
    queryKey: ['admin', 'departments', showArchived],
    queryFn: () => api.listDepartments(true),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.adminArchiveDepartment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'departments'] });
      qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => api.adminUpdateDepartment(id, { status: 'active' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'departments'] });
      qc.invalidateQueries({ queryKey: ['departments'] });
    },
  });

  const visible = departments.filter((d) => showArchived || d.status === 'active');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Departments</h1>
          <p className="text-slate-400 mt-1">Work is booked against these departments. Archiving keeps historical entries intact.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
              className="w-4 h-4 rounded border-slate-600" />
            Show archived
          </label>
          <button onClick={() => { setEditTarget(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-lg transition-colors">
            <PlusIcon className="w-4 h-4" /> New Department
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
          <RectangleGroupIcon className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No departments yet.</p>
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Name</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Slug</th>
                <th className="text-right px-4 py-3 text-slate-400 font-medium">Sort</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {visible.map((d) => (
                <tr key={d.id} className="hover:bg-slate-700/20 transition-colors">
                  <td className="px-4 py-3 text-white">{d.name}</td>
                  <td className="px-4 py-3 font-mono text-cyan-400 text-xs">{d.slug}</td>
                  <td className="px-4 py-3 text-right text-slate-300">{d.sort_order}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${d.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}`}>
                      {d.status === 'active' ? 'Active' : 'Archived'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => { setEditTarget(d); setShowModal(true); }}
                        className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors" title="Edit">
                        <PencilIcon className="w-3.5 h-3.5" />
                      </button>
                      {d.status === 'active' ? (
                        <button onClick={() => { if (confirm(`Archive ${d.name}?`)) archiveMutation.mutate(d.id); }}
                          className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded transition-colors" title="Archive">
                          <ArchiveBoxXMarkIcon className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <button onClick={() => restoreMutation.mutate(d.id)}
                          className="p-1.5 text-slate-400 hover:text-green-400 hover:bg-slate-700 rounded transition-colors" title="Restore">
                          <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && <DepartmentModal department={editTarget} onClose={() => { setShowModal(false); setEditTarget(null); }} />}
    </div>
  );
}

export default AdminDepartmentsPage;
